import { DOCUMENT } from '@angular/common';
import { inject, Service } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

@Service()
export class LinkService {
  private readonly document = inject<Document>(DOCUMENT);

  /** Tracks per-link loading observables to re-use. */
  private readonly linkState = new WeakMap<HTMLLinkElement, Observable<HTMLLinkElement>>();
  /**
   * Finds an existing link node in the document head.
   * @param rel The link rel attribute.
   * @param href The link href attribute.
   * @returns The found link element or undefined if not found.
   */
  find(rel: string, href: string): HTMLLinkElement {
    const head = this.document.getElementsByTagName('head')[0];
    const links = head.getElementsByTagName('link');

    for (let i = 0; i < links.length; i += 1) {
      const link = links[i];
      if (link.rel === rel && link.href === href) {
        return link;
      }
    }
  }

  /**
   * Injects a link node into the document head.
   * @param rel The link rel attribute.
   * @param href The link href attribute.
   * @returns An observable that completes when the link has loaded or errored.
   */
  inject$(rel: string, href: string): Observable<HTMLLinkElement> {
    // Create the link to inject
    const link = this.document.createElement('link');
    link.rel = rel;
    link.href = href;

    // Create an observer to track the loading state
    const link$ = new Observable<HTMLLinkElement>(observer => {
      try {
        link.onload = () => {
          observer.next(link);
          observer.complete();
        };

        link.onerror = (errorEvent: Event | string) => {
          observer.error(errorEvent);
          observer.complete();
        };

        const head = this.document.getElementsByTagName('head')[0];
        head.appendChild(link);
      } catch (ex) {
        observer.error(ex);
        observer.complete();
      }
    }).pipe(
      shareReplay(1)
    );

    // Track the observable for this link so we can re-use it if requested again through the load$ method
    this.linkState.set(link, link$);

    return link$;
  }

  /**
   * Loads a link node in the document head.
   * If the link already exists then it will be used instead of creating a new one.
   * The observable will complete when the link has loaded or errored.
   * If the link already exists and is loaded then the observable will complete immediately.
   * @param rel The link rel attribute.
   * @param href The link href attribute.
   * @return An observable that completes when the link has loaded or errored.
   */
  load$(rel: string, href: string): Observable<HTMLLinkElement> {
    const existingLink = this.find(rel, href);
    if (existingLink) {
      return this.linkState.get(existingLink);
    } else {
      return this.inject$(rel, href);
    }
  }

  /**
   * Removes a link node from the document head.
   * @param link The link element to remove.
   */
  remove(link: HTMLLinkElement) {
    // Detach handlers and forget the observable state
    link.onload = null;
    link.onerror = null;
    link.parentNode?.removeChild(link);
    this.linkState.delete(link);
  }
}
